import { Link } from "@mui/material";
import { capitalize } from "lodash";
import {
  NoteAccessLevel,
  ShipmentDocument,
} from "../../../../graphql/generated";
import { ShipmentDocumentInputData } from "../../../../redux/slices/Types";
import LynksTable, { TableField } from "../../../common/LynksTable/LynksTable";
import { useTranslation } from "react-i18next";
import { useMemo } from "react";
import { testIds } from "./DocumentsList.testIds";

export type DocumentType = Pick<
  ShipmentDocument,
  | "_id"
  | "url"
  | "accessLevel"
  | "name"
  | "isBillable"
  | "isForRatecons"
  | "type"
  | "isForReceivers"
> & {
  receiver: { _id: string; name: string };
} & {
  shipper: { _id: string; name: string };
};

export type DocumentsListProps = {
  onDelete?: (id: string) => void;
  documents: Array<DocumentType>;
  onChange: (documents: ShipmentDocumentInputData[]) => void;
  readonly?: boolean;
  onEdit?: (id: string) => void;
};

export default function DocumentsList({
  onDelete,
  documents,
  onChange,
  readonly,
  onEdit,
}: DocumentsListProps) {
  const { t } = useTranslation("orders");
  const noteTableFields: Array<TableField<DocumentType>> = useMemo(
    () => [
      {
        label: t("documents.document"),
        type: "custom",
        value: (document: DocumentType) => {
          return (
            <Link data-testid={testIds.documentsListOpenDocument} target="_blank" component="a" href={document.url}>
              {document.name}
            </Link>
          );
        },
      },
      {
        label: t("documents.visibility"),
        type: "string",
        value: (document: DocumentType) => {
          if (
            document.accessLevel.length === Object.keys(NoteAccessLevel).length
          ) {
            return "All";
          } else {
            return capitalize(document.accessLevel.join(", "));
          }
        },
      },
      {
        label: t("documents.location"),
        type: "string",
        value: (document: DocumentType) => {
          return document.shipper?.name || document.receiver?.name;
        },
      },
      {
        label: t("documents.includeForRatecons", "Include in Ratecon"),
        type: "boolean",
        value: "isForRatecons",
      },
      {
        label: t("documents.includeForInvoices", "Include for Invoicing"),
        type: "boolean",
        value: "isBillable",
      },
      {
        label: t("documents.includeForReceivers", "Include for Receivers"),
        type: "boolean",
        value: "isForReceivers",
      },
    ],
    [t]
  );

  return (
    <LynksTable
      fields={noteTableFields}
      data={documents}
      showEmptyStateImage={false}
      onDelete={!readonly ? onDelete : undefined}
      onEdit={!readonly ? onEdit : undefined}
      onRowChange={
        !readonly
          ? (id, updatedDocument) => {
              onChange(
                documents
                  .map((document) =>
                    document._id === id ? updatedDocument : document
                  )
                  .map((doc) => ({
                    ...doc,
                    shipper: doc.shipper._id || null,
                    receiver: doc.receiver._id || null,
                  }))
              );
            }
          : undefined
      }
    />
  );
}
